import { BarChart3, type LucideIcon, Mail, Palette, Rocket, Sparkles, Target, TrendingUp } from 'lucide-react'
import type { ComponentProps } from 'react'

// Icon mapping for guaranteed uniqueness per slug
const SLUG_ICONS: Record<string, LucideIcon> = {
  'getting-started': Rocket,
  'best-practices': Target,
  'case-study': TrendingUp,
  'analytics-tracking': BarChart3,
  'custom-styling': Palette,
  'email-integration': Mail,
}

// Content-based icon matchers
const ICON_MATCHERS: Array<{ keywords: string[]; icon: LucideIcon }> = [
  { keywords: ['getting started', 'tutorial'], icon: Rocket },
  { keywords: ['best practice', 'strategy'], icon: Target },
  { keywords: ['case study', 'growth'], icon: TrendingUp },
  { keywords: ['analytics', 'tracking'], icon: BarChart3 },
  { keywords: ['styling', 'design', 'custom'], icon: Palette },
  { keywords: ['email', 'integration'], icon: Mail },
]

function selectIcon(title: string, description: string, tags?: string[], slug?: string): LucideIcon {
  // Priority 1: Slug-based mapping
  if (slug && SLUG_ICONS[slug]) {
    return SLUG_ICONS[slug]
  }

  // Priority 2: Content-based matching
  const searchText = `${title} ${description} ${tags?.join(' ') || ''}`.toLowerCase()
  const match = ICON_MATCHERS.find(({ keywords }) => keywords.some((keyword) => searchText.includes(keyword)))

  return match?.icon ?? Sparkles
}

interface BlogIconProps extends ComponentProps<LucideIcon> {
  title: string
  description: string
  tags?: string[]
  slug?: string
}

/**
 * BlogIcon component - renders the appropriate icon for a blog post
 *
 * @example
 * ```tsx
 * <BlogIcon
 *   title="Getting Started"
 *   description="Learn the basics"
 *   slug="getting-started"
 *   className="w-12 h-12"
 * />
 * ```
 */
export function BlogIcon({ title, description, tags, slug, ...props }: BlogIconProps) {
  const Icon = selectIcon(title, description, tags, slug)
  return <Icon {...props} />
}

// Backward compatibility - can be removed once all usages are migrated
export function getBlogIcon(title: string, description: string, tags?: string[], slug?: string): LucideIcon {
  return selectIcon(title, description, tags, slug)
}
